import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  History, 
  CheckCircle2, 
  Clock, 
  Eye, 
  AlertCircle,
  Users,
  FileText,
  ExternalLink
} from "lucide-react";
import { formatUtcTimestamp } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { formatReportDate } from "@/lib/utils";

interface DiagnosticsSummary {
  headerTotal: number | null;
  parsedTotal: number | null;
  countsMatch: boolean;
}

interface ReportUploadSummary {
  id: string;
  uploadedAt: string;
  uploadSequence: number;
  isCurrentForDate: boolean;
  supersededByReportId: string | null;
  totalAnimals: number;
  sourceFileHash: string | null;
  diagnosticsSummary?: DiagnosticsSummary;
}

interface ReportHistoryResponse {
  location: "SALEM" | "BOSTON" | "METHUEN" | "CAPE";
  reportDate: string;
  type: "daily";
  totalUploads: number;
  currentUploadId: string;
  uploads: ReportUploadSummary[];
}

interface CurrentReportInfo {
  id: string;
  location: string;
  reportDate: string;
  uploadSequence: number;
  isCurrentForDate: boolean;
}

interface ReportHistoryDrawerProps {
  reportId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentReport: CurrentReportInfo;
}

function formatLocationDisplay(location: string): string {
  const displays: Record<string, string> = {
    SALEM: "Salem",
    BOSTON: "Boston",
    METHUEN: "Methuen",
    CAPE: "Cape",
  };
  return displays[location] || location;
}

export function ReportHistoryDrawer({
  reportId,
  open,
  onOpenChange,
  currentReport,
}: ReportHistoryDrawerProps) {
  const [, setLocation] = useLocation();

  const { data: history, isLoading, error } = useQuery<ReportHistoryResponse>({
    queryKey: ["/api/reports", reportId, "history"],
    enabled: open && !!reportId,
  });

  const handleViewVersion = (versionId: string) => {
    onOpenChange(false);
    setLocation(`/reports/${versionId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md overflow-y-auto" 
        data-testid="report-history-drawer"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Report History
          </SheetTitle>
          <SheetDescription>
            All uploads for this date and location
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4" data-testid="history-loading">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : error ? (
          <div className="py-8 text-center" data-testid="history-error">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-3" />
            <p className="text-muted-foreground">
              Unable to load history. Please try again.
            </p>
          </div>
        ) : history ? (
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Location</span>
                    <span className="font-medium" data-testid="history-location">
                      {formatLocationDisplay(history.location)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Report Date</span>
                    <span className="font-medium" data-testid="history-date">
                      {formatReportDate(history.reportDate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total Uploads</span>
                    <Badge variant="secondary" data-testid="history-total-uploads">
                      {history.totalUploads}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Upload History
              </h3>

              {history.uploads.map((upload) => (
                <Card 
                  key={upload.id} 
                  className={upload.id === currentReport.id ? "ring-2 ring-primary" : ""}
                  data-testid={`history-version-${upload.uploadSequence}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            Version {upload.uploadSequence}
                          </span>
                          {upload.isCurrentForDate ? (
                            <Badge 
                              variant="default" 
                              className="gap-1"
                              data-testid="history-current-badge"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Current
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <Clock className="h-3 w-3" />
                              Earlier
                            </Badge>
                          )}
                          {upload.id === currentReport.id && (
                            <Badge variant="outline" className="text-xs">
                              Viewing
                            </Badge>
                          )}
                        </div>

                        <div className="text-sm text-muted-foreground">
                          Uploaded {formatUtcTimestamp(upload.uploadedAt)}
                        </div>

                        <div className="flex items-center gap-4 text-sm mt-2">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Users className="h-3.5 w-3.5" />
                            <span data-testid={`history-animals-${upload.uploadSequence}`}>
                              {upload.totalAnimals} animals
                            </span>
                          </div>

                          {upload.diagnosticsSummary && upload.diagnosticsSummary.parsedTotal !== null && (
                            <div className="text-muted-foreground text-xs">
                              {upload.diagnosticsSummary.headerTotal !== null ? (
                                <span>
                                  Header: {upload.diagnosticsSummary.headerTotal} / 
                                  Parsed: {upload.diagnosticsSummary.parsedTotal}
                                  {upload.diagnosticsSummary.countsMatch ? (
                                    <CheckCircle2 className="inline h-3 w-3 ml-1 text-green-500" />
                                  ) : (
                                    <AlertCircle className="inline h-3 w-3 ml-1 text-amber-500" />
                                  )}
                                </span>
                              ) : (
                                <span>Parsed: {upload.diagnosticsSummary.parsedTotal}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {upload.id !== currentReport.id && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewVersion(upload.id)}
                            data-testid={`button-view-version-${upload.uploadSequence}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          data-testid={`button-view-pdf-${upload.uploadSequence}`}
                        >
                          <a
                            href={`/api/reports/${upload.id}/file`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FileText className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {history.uploads.length === 0 && (
                <div className="py-4 text-center text-muted-foreground">
                  No upload history available.
                </div>
              )}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
